import { useState } from 'react'
import { ChevronDown, Loader2, AlertTriangle } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useInvoices } from '@/hooks/useInvoices'
import { usePatientAnalytics } from '@/hooks/usePatientAnalytics'
import { useBranch } from '@/providers/branch-provider'
import { formatCurrency } from '@/lib/utils'

// Branch color mapping
const branchColors: Record<string, string> = {
  'bangi': '#10b981',
  'putrajaya': '#4a8fe8',
  'shah-alam': '#f59e0b',
  'kuala-lumpur': '#8b5cf6',
}

// Format date to "DD MMM, YYYY"
function formatDate(dateStr: string): string {
  const date = new Date(dateStr)
  return date.toLocaleDateString('en-MY', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  })
}

export function RecentPatients() {
  const [isInvoicesExpanded, setIsInvoicesExpanded] = useState(true)
  const [isInactiveExpanded, setIsInactiveExpanded] = useState(false)

  const { branchId } = useBranch()
  const branchIdNum = branchId === 'all' ? undefined : parseInt(branchId.replace(/\D/g, '')) || undefined

  // Fetch recent 5 invoices
  const { data: invoicesData, isPending: isLoadingInvoices } = useInvoices({
    branchId: branchIdNum,
    pageSize: 5,
    sortBy: 'invoiceDate',
    sortOrder: 'desc',
  })

  // Fetch inactive patients (6+ months)
  const { data: patientsData, isPending: isLoadingPatients } = usePatientAnalytics({
    branchId: branchId === 'all' ? undefined : branchId,
    inactiveMonths: 6,
  })

  const recentInvoices = invoicesData?.data ?? []
  const inactivePatients = patientsData?.inactivePatients?.slice(0, 5) ?? []

  return (
    <div className="bg-card rounded-xl p-6">
      {/* Recent Invoices Section */}
      <div className="flex items-center justify-between">
        <button
          onClick={() => setIsInvoicesExpanded(!isInvoicesExpanded)}
          className="flex items-center gap-2.5 cursor-pointer py-1.5 select-none"
        >
          <ChevronDown
            className={cn(
              'w-[18px] h-[18px] text-text-secondary transition-transform duration-200',
              !isInvoicesExpanded && '-rotate-90'
            )}
          />
          <span className="text-[15px] font-semibold text-text-dark">Recent Invoices</span>
          <span className="bg-muted px-2.5 py-0.5 rounded-full text-[13px] font-medium text-text-secondary">
            {isLoadingInvoices ? '...' : recentInvoices.length}
          </span>
        </button>
      </div>

      {/* Invoices Table */}
      {isInvoicesExpanded && (
        <div className="mt-4">
          {isLoadingInvoices ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-5 h-5 animate-spin text-text-muted" />
            </div>
          ) : recentInvoices.length === 0 ? (
            <div className="text-center py-8 text-text-muted text-sm">
              No recent invoices
            </div>
          ) : (
            <>
              {/* Table Header */}
              <div className="grid grid-cols-[1fr_80px_100px_100px_1fr] gap-4 px-4 py-3 bg-muted rounded-t-lg border-b border-border">
                <span className="text-[12px] font-medium text-text-muted uppercase tracking-wide">Patient</span>
                <span className="text-[12px] font-medium text-text-muted uppercase tracking-wide">Branch</span>
                <span className="text-[12px] font-medium text-text-muted uppercase tracking-wide">Amount</span>
                <span className="text-[12px] font-medium text-text-muted uppercase tracking-wide">Date</span>
                <span className="text-[12px] font-medium text-text-muted uppercase tracking-wide">Invoice</span>
              </div>

              {/* Table Body */}
              <div className="divide-y divide-border-light">
                {recentInvoices.map((invoice) => {
                  const branchColor = branchColors[invoice.branchCode ?? ''] ?? '#6b7280'
                  return (
                    <div key={invoice.id} className="grid grid-cols-[1fr_80px_100px_100px_1fr] gap-4 px-4 py-4 items-center">
                      <div className="flex items-center gap-3">
                        <div className="w-1 h-5 rounded-sm bg-gold" />
                        <span className="text-[13px] font-medium text-text-dark truncate">
                          {invoice.patientName ?? 'Unknown'}
                        </span>
                      </div>
                      <div
                        className="w-8 h-8 rounded-lg flex items-center justify-center text-[12px] font-bold text-white"
                        style={{ background: branchColor }}
                        title={invoice.branchName ?? ''}
                      >
                        {(invoice.branchName ?? 'U').charAt(0)}
                      </div>
                      <span className="text-[13px] font-semibold text-text-dark">
                        {formatCurrency(parseFloat(invoice.grandTotal ?? '0'))}
                      </span>
                      <span className="text-[13px] text-text-primary">
                        {formatDate(invoice.invoiceDate)}
                      </span>
                      <span className="text-[13px] text-text-muted truncate">
                        {invoice.invoiceNo}
                      </span>
                    </div>
                  )
                })}
              </div>
            </>
          )}
        </div>
      )}

      {/* Separator */}
      <div className={cn('h-px bg-border', isInvoicesExpanded ? 'mt-4 mb-4' : 'mt-3 mb-3')} />

      {/* Inactive Patients Alert Section */}
      <div className="flex items-center justify-between">
        <button
          onClick={() => setIsInactiveExpanded(!isInactiveExpanded)}
          className="flex items-center gap-2.5 cursor-pointer py-1.5 select-none"
        >
          <ChevronDown
            className={cn(
              'w-[18px] h-[18px] text-text-secondary transition-transform duration-200',
              !isInactiveExpanded && '-rotate-90'
            )}
          />
          <AlertTriangle className="w-4 h-4 text-warning" />
          <span className="text-[15px] font-semibold text-text-dark">Inactive Patients</span>
          <span className="bg-warning/10 text-warning px-2.5 py-0.5 rounded-full text-[13px] font-medium">
            {isLoadingPatients ? '...' : `${patientsData?.summary?.inactiveCount ?? 0} total`}
          </span>
        </button>
      </div>

      {/* Inactive Patients Table */}
      {isInactiveExpanded && (
        <div className="mt-4">
          {isLoadingPatients ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-5 h-5 animate-spin text-text-muted" />
            </div>
          ) : inactivePatients.length === 0 ? (
            <div className="text-center py-8 text-text-muted text-sm">
              No inactive patients (6+ months)
            </div>
          ) : (
            <>
              {/* Table Header */}
              <div className="grid grid-cols-[1fr_80px_100px_100px_100px] gap-4 px-4 py-3 bg-warning/5 rounded-t-lg border-b border-border">
                <span className="text-[12px] font-medium text-text-muted uppercase tracking-wide">Patient</span>
                <span className="text-[12px] font-medium text-text-muted uppercase tracking-wide">Branch</span>
                <span className="text-[12px] font-medium text-text-muted uppercase tracking-wide">Last Visit</span>
                <span className="text-[12px] font-medium text-text-muted uppercase tracking-wide">Days</span>
                <span className="text-[12px] font-medium text-text-muted uppercase tracking-wide">Total Spent</span>
              </div>

              {/* Table Body */}
              <div className="divide-y divide-border-light">
                {inactivePatients.map((patient) => {
                  // Try to get branch color from name
                  const branchKey = (patient.branchName ?? '').toLowerCase().replace(/\s+/g, '-')
                  const branchColor = branchColors[branchKey] ?? '#6b7280'

                  return (
                    <div key={patient.id} className="grid grid-cols-[1fr_80px_100px_100px_100px] gap-4 px-4 py-4 items-center">
                      <div className="flex items-center gap-3">
                        <div className="w-1 h-5 rounded-sm bg-warning" />
                        <div className="min-w-0">
                          <span className="text-[13px] font-medium text-text-dark block truncate">
                            {patient.name}
                          </span>
                          <span className="text-[11px] text-text-muted">{patient.pno}</span>
                        </div>
                      </div>
                      <div
                        className="w-8 h-8 rounded-lg flex items-center justify-center text-[12px] font-bold text-white"
                        style={{ background: branchColor }}
                        title={patient.branchName ?? ''}
                      >
                        {(patient.branchName ?? 'U').charAt(0)}
                      </div>
                      <span className="text-[13px] text-text-primary">
                        {formatDate(patient.lastVisit)}
                      </span>
                      <DaysBadge days={patient.daysSinceVisit} />
                      <span className="text-[13px] font-semibold text-text-dark">
                        {formatCurrency(patient.totalSpent)}
                      </span>
                    </div>
                  )
                })}
              </div>

              {/* View all link */}
              {(patientsData?.summary?.inactiveCount ?? 0) > 5 && (
                <div className="px-4 py-3 text-center">
                  <span className="text-[13px] text-primary cursor-pointer hover:underline">
                    View all {patientsData?.summary?.inactiveCount} inactive patients →
                  </span>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  )
}

// Days Badge Component - shows urgency based on inactivity
function DaysBadge({ days }: { days: number }) {
  let colorClass = 'text-warning border-warning'
  if (days > 365) {
    colorClass = 'text-destructive border-destructive'
  } else if (days > 270) {
    colorClass = 'text-orange-500 border-orange-500'
  }

  return (
    <span
      className={cn(
        'inline-flex items-center px-2 py-0.5 rounded-full text-[12px] font-medium border bg-transparent',
        colorClass
      )}
    >
      {days}d
    </span>
  )
}
